import { type ReactNode, useCallback, useMemo, type FC } from "react";
import {
  continueWhenNoUnselected,
  FintechMarketplacePage,
  FintechMarketplaceProductsFetcher,
} from "@b2bportal/core-fintech-ui";
import { CfarFintechMarketplaceProductConfig } from "@b2bportal/core-flights-cfar-offers";
import { CallState, type ISelectedTrip } from "@hopper-b2b/types";

interface UseFlightsFintechMarketplaceProps {
  selectedTrip?: ISelectedTrip | undefined;
  goToPrevStep: () => void;
  goToNextStep: (replace?: boolean) => void;
}

export const useFlightsFintechMarketplace = (
  props: UseFlightsFintechMarketplaceProps
) => {
  const { selectedTrip, goToPrevStep, goToNextStep } = props;

  const stableSelectedTrip = useMemo(
    () =>
      selectedTrip
        ? {
            tripId: selectedTrip.tripId,
            returnFareId: selectedTrip.returnFareId,
            outgoingFareId: selectedTrip.outgoingFareId,
          }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedTrip?.tripId,
      selectedTrip?.returnFareId,
      selectedTrip?.outgoingFareId,
    ]
  );

  const useInitProducts = useMemo(
    () =>
      useFlightsFintechMarketplaceInitProducts.bind(
        undefined,
        stableSelectedTrip
      ),
    [stableSelectedTrip]
  );

  const FlightsFintechMarketplacePage = useMemo(
    () => () => (
      <FintechMarketplacePage
        PageWrapper={({ children }) => <>{children}</>}
        //NOTE: this component is mobile only for now, so we don't need to pass the TripDetails and PriceBreakdown
        TripDetails={() => null}
        PriceBreakdown={() => null}
        canContinue={continueWhenNoUnselected}
        products={{
          [CfarFintechMarketplaceProductConfig.name]:
            CfarFintechMarketplaceProductConfig.View,
        }}
        productsOrder={[CfarFintechMarketplaceProductConfig.name]}
        useInitProducts={useInitProducts}
        goToPrevStep={goToPrevStep}
        onNoProductOffers={() => goToNextStep(true)}
        goToNextStep={() => goToNextStep(false)}
      />
    ),
    [goToNextStep, goToPrevStep, useInitProducts]
  );

  const FlightsFintechMarketplaceProductsFetcher = useMemo(
    (): FC<{ children: ReactNode }> =>
      ({ children }) => (
        <FintechMarketplaceProductsFetcher
          useInitProducts={useInitProducts}
          fetchWhenCallState={new Set([CallState.NotCalled, CallState.Success])}
        >
          {children}
        </FintechMarketplaceProductsFetcher>
      ),
    [useInitProducts]
  );

  return {
    FlightsFintechMarketplacePage,
    FlightsFintechMarketplaceProductsFetcher,
  };
};

const useFlightsFintechMarketplaceInitProducts = (
  selectedTrip: Pick<
    ISelectedTrip,
    "tripId" | "returnFareId" | "outgoingFareId"
  >
) => {
  const [cfarOffers, fetchCfarOffers] =
    CfarFintechMarketplaceProductConfig.useInit({
      baseProduct: {
        selectedTrip,
      },
    });

  return useMemo(
    () => ({
      productOffers: {
        [CfarFintechMarketplaceProductConfig.name]: cfarOffers,
      },
      fetchProductOffers: {
        [CfarFintechMarketplaceProductConfig.name]: fetchCfarOffers,
      },
    }),
    [cfarOffers, fetchCfarOffers]
  );
};
